import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MoonStar } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FaqSection from "../components/FaqSection";
import GuidebookCard from "../components/GuidebookCard";
import { SHOP_URL, IMAGES, AP_PILLARS, AP_FAQ, GUIDEBOOKS } from "../data/content";

function Overview() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <h2 className="font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">
              Beyond the
              <br />
              body, deliberately.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal>
            <p className="body-muted text-base sm:text-lg">
              Unlike remote viewing — where the observer remains anchored in the
              physical vessel — astral projection is the full experiential
              separation of conscious awareness into what practitioners
              describe as the non-physical subtle continuum.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="body-muted mt-6 text-sm sm:text-base">
              The Gateway-era materials treat it as a learnable state with
              recognizable checkpoints: the body-asleep/mind-awake boundary, the
              vibrational threshold, and a set of separation techniques
              practiced calmly, at your own pace, on your own terms.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 border border-white/[0.08] bg-[#0F121B]/70 p-7 sm:p-8">
              <p className="flex items-center gap-3 text-[11px] font-mono-ui uppercase tracking-[0.22em] text-[#A78BFA]">
                <MoonStar className="h-4 w-4" /> What practice needs
              </p>
              <ul className="mt-6 grid gap-3 text-sm text-[#8E95A5] sm:grid-cols-2">
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>A consistent sleep-edge routine</li>
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>A dark, undisturbed room</li>
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>Patience with the vibrational stage</li>
                <li className="flex gap-3"><span className="text-[#38BDF8]">—</span>A log of exits and re-entries</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section className="relative border-t border-white/[0.06] bg-[#0C0E14]/40 py-24 lg:py-32" data-testid="ap-pillars-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <Reveal>
          <p className="eyebrow">The Method</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display mt-6 max-w-2xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
            Four pillars of conscious exit.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px border border-white/[0.07] bg-white/[0.07] sm:grid-cols-2">
          {AP_PILLARS.map((p, i) => (
            <Reveal
              key={p.step}
              delay={0.08 * i}
              className="group bg-[#0A0C12] p-8 transition-colors duration-500 hover:bg-[#0F121B] lg:p-10"
            >
              <p className="font-mono-ui text-sm tracking-[0.3em] text-[#38BDF8]">
                {p.step}
              </p>
              <h3 className="font-display mt-8 text-2xl leading-snug text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA]">
                {p.name}
              </h3>
              <p className="body-muted mt-4 text-sm leading-relaxed">{p.detail}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GuidebookPathways() {
  const books = GUIDEBOOKS.filter((b) => b.category === "Astral Projection");
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div>
          <Reveal>
            <p className="eyebrow">Guidebook Pathways</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-6 max-w-2xl text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl lg:text-5xl">
              Manuals for the vibrational path.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="body-muted max-w-sm text-sm leading-relaxed">
            Full night-routine logs, vibrational calibration drills, and
            navigational safety frameworks.
          </p>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {books.map((book, i) => (
          <Reveal key={book.id} delay={0.1 * i} className="h-full">
            <GuidebookCard book={book} />
          </Reveal>
        ))}
        <Reveal delay={0.25} className="h-full">
          <Link
            to="/guidebooks"
            data-testid="ap-all-guidebooks-btn"
            className="group flex h-full min-h-[220px] flex-col justify-between border border-dashed border-white/15 p-8 transition-all duration-300 hover:border-[#8B5CF6]/60"
          >
            <p className="font-display text-2xl text-[#F0F2F8]">
              See the full
              <br />
              library.
            </p>
            <span className="inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#A78BFA]">
              All guidebooks
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ContinueLearning() {
  return (
    <section className="relative border-t border-white/[0.07]">
      <div className="mx-auto grid max-w-7xl gap-px bg-white/[0.07] sm:grid-cols-2 lg:grid-cols-3">
        <Link
          to="/remote-viewing"
          data-testid="ap-continue-rv-btn"
          className="group bg-[#0A0C12] p-10 transition-colors duration-500 hover:bg-[#0F121B] lg:p-12"
        >
          <p className="eyebrow">Next Discipline</p>
          <p className="font-display mt-5 text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:text-3xl">
            Continue to Remote Viewing
          </p>
          <span className="mt-6 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#38BDF8]">
            Take the coordinates
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </Link>
        <Link
          to="/faq"
          data-testid="ap-continue-faq-btn"
          className="group bg-[#0A0C12] p-10 transition-colors duration-500 hover:bg-[#0F121B] lg:p-12"
        >
          <p className="eyebrow">Before You Begin</p>
          <p className="font-display mt-5 text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:text-3xl">
            Read the common inquiries
          </p>
          <span className="mt-6 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#38BDF8]">
            Open the FAQ
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </Link>
        <a
          href={SHOP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="ap-continue-shop-btn"
          className="group bg-[#0A0C12] p-10 transition-colors duration-500 hover:bg-[#0F121B] lg:p-12"
        >
          <p className="eyebrow">The Storefront</p>
          <p className="font-display mt-5 text-2xl text-[#F0F2F8] transition-colors duration-300 group-hover:text-[#A78BFA] sm:text-3xl">
            Get the manual, start tonight
          </p>
          <span className="mt-6 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#38BDF8]">
            Fourthwall Shop <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </a>
      </div>
    </section>
  );
}

export default function AstralProjection() {
  usePageMeta(
    "Astral Projection — The Vibrational Path | Consciousness Ascending",
    "Learn astral projection: the four pillars of conscious exit, vibrational-state guidance, and guidebooks. Purchases via our official Fourthwall shop."
  );
  return (
    <>
      <PageHero
        eyebrow="Discipline 02 // Astral Projection"
        lines={["Cross the vibrational", "threshold."]}
        description="Sovereign beyond-body consciousness. Astral projection is the deliberate, learnable transition of awareness out of the physical vessel — through the vibrational threshold and into the subtle continuum."
        image={IMAGES.ap}
        imageAlt="Field Manual // Beyond-body transit"
        meta="STATE: MIND AWAKE // BODY ASLEEP // SOVEREIGN EXIT"
      />
      <Overview />
      <Pillars />
      <GuidebookPathways />
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:px-10 lg:py-32">
        <Reveal>
          <p className="eyebrow">Guidance</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display mt-6 text-3xl font-light tracking-tight sm:text-4xl">
            Astral projection, questioned.
          </h2>
        </Reveal>
        <Reveal delay={0.2} className="mt-12">
          <FaqSection items={AP_FAQ} />
        </Reveal>
      </section>
      <ContinueLearning />
    </>
  );
}
